import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Text, Badge, Flex, Divider, Spinner } from "@chakra-ui/react";
import ReusableDrawer from "components/Drawers/ReusableDrawer";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import { Trash, EyeOff, BellOff, EllipsisVertical } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useDispatch } from "react-redux";
import { markNotificationAsReadAction, fetchNotificationsAction } from "state/actions/notificationActions";
import LoadingSpinner from "components/Loading/LoadingSpinner";
import ResolveSupportNotificationModal from "./ResolveSupportNotificationModal";

const NotificationDrawer = ({
  isOpen,
  onClose,
  notifications,
  loading,
  loadMore,
  hasMore,
}) => {
  const dispatch = useDispatch();
  const [expandedNotification, setExpandedNotification] = useState(null);
  const [localNotifications, setLocalNotifications] = useState(notifications || []);
  const [isResolveModalOpen, setResolveModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const observer = useRef(null);

  const headerContent = "Notifications";

  const notificationMenuItems = [
    { icon: <Trash size={16} />, text: "Delete notification" },
    { icon: <EyeOff size={16} />, text: "Show less like this" },
    { icon: <BellOff size={16} />, text: "Turn off this notification type" },
  ];

  // Update localNotifications when props.notifications change
  useEffect(() => {
    if (!notifications) return;

    setLocalNotifications(prev => {
      const map = new Map();

      [...prev, ...notifications].forEach((n) => {
        map.set(n.notification_id, n);
      });

      return Array.from(map.values());
    });
  }, [notifications]);


  // Load first page when drawer opens and notifications empty
  useEffect(() => {
    if (isOpen && !loading && hasMore && localNotifications.length === 0) {
      loadMore();
    }
  }, [isOpen, loading, hasMore, localNotifications.length, loadMore]);

  // Intersection observer to load more on scroll near bottom
  const lastNotificationRef = useCallback(
    (node) => {
      if (loading) return; // Do not attach or trigger while loading
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, loadMore]
  );

  // Toggle expand and mark read
  const toggleNotification = async (notificationId) => {
    const notification = localNotifications.find(
      (n) => n.notification_id === notificationId
    );

    if (!notification) {
      console.error("Notification not found!");
      return;
    }

    setExpandedNotification((prev) =>
      prev === notificationId ? null : notificationId
    );

    // Customer Support notifications
    if (
      notification.notification_type === "Customer Support" &&
      notification.read_status === "Unread"
    ) {
      setSelectedNotification(notification);
      setResolveModalOpen(true);
      return;
    }

    if (notification.read_status === "Unread") {
      try {
        setLocalNotifications((prev) =>
          prev.map((n) =>
            n.notification_id === notificationId
              ? {
                ...n,
                read_status: "Read",
              }
              : n
          )
        );

        await dispatch(
          markNotificationAsReadAction(
            notificationId
          )
        );

        dispatch(
          fetchNotificationsAction()
        );
      } catch (error) {
        // Rollback
        setLocalNotifications((prev) =>
          prev.map((n) =>
            n.notification_id === notificationId
              ? {
                ...n,
                read_status: "Unread",
              }
              : n
          )
        );

        console.error(
          "Failed to mark notification as read:",
          error
        );
      }
    }
  };

  const handleResolveSupportNotification =
    async () => {
      if (!selectedNotification) return;

      try {
        await dispatch(
          markNotificationAsReadAction(
            selectedNotification.notification_id
          )
        );
        setLocalNotifications(prev =>
          prev.map(n =>
            n.notification_id === selectedNotification.notification_id
              ? { ...n, read_status: "Read" }
              : n
          )
        );

        dispatch(
          fetchNotificationsAction()
        );

        setResolveModalOpen(false);

        setSelectedNotification(
          null
        );
      } catch (error) {
        console.error(error);
      }
    };

  const getTruncatedMessage = (message) => {
    const words = message.split(" ");
    return words.length > 3 ? `${words.slice(0, 4).join(" ")}...` : message;
  };

  const getRelativeTime = (timestamp) => {
    const sentAt = new Date(timestamp);
    const gmtPlus2Date = new Date(sentAt.getTime() + 2 * 60 * 60 * 1000);
    return formatDistanceToNow(gmtPlus2Date, { addSuffix: true });
  };

  const sortedNotifications = [...localNotifications].sort(
    (a, b) => new Date(b.sent_at) - new Date(a.sent_at)
  );

  const bodyContent = (
    <Box>
      {/* Initial loading spinner */}
      {loading && localNotifications.length === 0 ? (
        <Flex justifyContent="center" py={4}>
          <Spinner size="lg" />
        </Flex>
      ) : sortedNotifications.length === 0 ? (
        <Text color="gray.500">No notifications available.</Text>
      ) : (
        <>
          {sortedNotifications.map((notification, index) => (
            <React.Fragment key={notification.notification_id}>
              <Box
                cursor="pointer"
                onClick={() => toggleNotification(notification.notification_id)}
                ref={index === sortedNotifications.length - 1 ? lastNotificationRef : null}
              >
                <Flex justifyContent="space-between" alignItems="center">
                  <Flex alignItems="center">
                    {notification.read_status === "Unread" && (
                      <Box
                        width="9px"
                        height="9px"
                        bg="#FAA61A"
                        borderRadius="50%"
                        mr={2}
                      />
                    )}
                    <Text
                      fontWeight="bold"
                      fontSize="14px"
                      color={notification.read_status === "Read" ? "gray.500" : "black"}
                    >
                      {notification.notification_type}
                    </Text>
                  </Flex>
                  <ThreeDotMenu
                    icon={
                      <EllipsisVertical
                        size={20}
                        color={notification.read_status === "Read" ? "gray" : "#071D54"}
                      />
                    }
                    items={notificationMenuItems}
                  />
                </Flex>

                <Flex mt={2} color={notification.read_status === "Read" ? "gray.500" : "gray.600"} fontSize="14px">
                  <Text>
                    {expandedNotification === notification.notification_id
                      ? notification.message
                      : getTruncatedMessage(notification.message)}
                  </Text>
                </Flex>

                <Flex mt={2} justifyContent="space-between" alignItems="center">
                  <Text fontSize="sm" color="gray.500">
                    {getRelativeTime(notification.sent_at)}
                  </Text>
                  <Badge
                    bg={notification.read_status === "Read" ? "gray.500" : "#071D54"}
                    color="white"
                    fontWeight="normal"
                    mr={4}
                  >
                    {notification.read_status === "Read" ? "Viewed" : "Unread"}
                  </Badge>
                </Flex>
              </Box>

              {index < sortedNotifications.length - 1 && <Divider my={4} />}
            </React.Fragment>
          ))}

          {/* Bottom spinner when loading more pages */}
          {loading && localNotifications.length > 0 && (
            <Flex justifyContent="center" mt={4}>
              <LoadingSpinner />
            </Flex>
          )}
        </>
      )}
    </Box>
  );

  return (
    <>
      <ReusableDrawer
        isOpen={isOpen}
        onClose={onClose}
        headerContent={headerContent}
        bodyContent={bodyContent}
      />
      <ResolveSupportNotificationModal
        isOpen={isResolveModalOpen}
        onClose={() =>
          setResolveModalOpen(false)
        }
        onConfirm={
          handleResolveSupportNotification
        }
      />
    </>

  );
};

export default NotificationDrawer;
